import { createContext, useContext, useEffect, useState } from "react"
import { iProvider, iRegionAndLocationContext, tGroupedRegionAndLocation, tGroupedRegionWithDistricts, tGroupedDistrict } from "../types";
import { District, Location, RegionalDivision } from "../types/schema";
import { authClient } from "../utils/amplifyClient";
import { useUserContext } from "./UserContext";
import {
    getInterimDistrictParentId,
    isInterimDistrictLocation,
} from "../utils/regionLocationHierarchy";

export const RegionAndLocationContext = createContext<iRegionAndLocationContext>({
    allRegions: [],
    allLocations: [],
    companyRegions: [],
    companyLocations: [],
    companyDistricts: [],
    companyGroupedRegionsAndLocations: [],
    companyGroupedData: [],
    loading: false,
    refreshRegionAndLocation: async () => { },
    resetRegionAndLocation: () => { },
    addRegion: () => { },
    updateRegion: () => { },
    addDistrict: () => { },
    updateDistrict: () => { },
    deleteDistrict: () => { },
    addLocation: () => { },
    updateLocation: () => { },
    updateLocationInDatabase: () => { },
    updateRegionInDatabase: () => { },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useRegionAndLocationContext = () => useContext(RegionAndLocationContext);

export const RegionAndLocationProvider = ({ children }: iProvider) => {

    const { companyUser, companyOwner } = useUserContext();
    const [companyRegions, setCompanyRegions] = useState<RegionalDivision[]>([]);
    const [companyLocations, setCompanyLocations] = useState<Location[]>([]);
    const [companyDistricts, setCompanyDistricts] = useState<District[]>([]);
    const [companyGroupedRegionsAndLocations, setCompanyGroupedRegionsAndLocations] = useState<tGroupedRegionAndLocation[]>([]);
    const [companyGroupedData, setCompanyGroupedData] = useState<tGroupedRegionWithDistricts[]>([]);
    const [allRegions, setAllRegions] = useState<RegionalDivision[]>([]);
    const [allLocations, setAllLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(false);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    useEffect(() => {
        if (companyId) {
            refreshRegionAndLocation();
        } else {
            resetRegionAndLocation();
        }
    }, [companyId]);

    const fetchAllRegionAndLocationData = async (): Promise<void> => {
        try {
            const [regionsResult, locationsResult] = await Promise.all([
                authClient.models.RegionalDivision.list(),
                authClient.models.Location.list(),
            ]);

            setAllRegions(regionsResult.data as RegionalDivision[]);
            setAllLocations(locationsResult.data as Location[]);
        } catch (error) {
            console.error('Failed to fetch all region/location data:', error);
        }
    };

    const refreshRegionAndLocation = async (): Promise<void> => {
        if (!companyId) return;
        setLoading(true);
        try {
            const { data: regions } = await authClient.models.RegionalDivision.listRegionalDivisionByCompanyId({ companyId });

            if (regions.length === 0) {
                setCompanyRegions([]);
                setCompanyDistricts([]);
                setCompanyLocations([]);
                setCompanyGroupedRegionsAndLocations([]);
                setCompanyGroupedData([]);
                return;
            }

            // Prefer company-scoped list so migrated Gen1 locations still appear even if the
            // regionalDivisionId GSI is incomplete. Group by regionalDivisionId in memory.
            const { data: companyLocationRows } = await authClient.models.Location.listLocationByCompanyId({
                companyId,
            });
            const locations = (companyLocationRows || []) as Location[];
            const districtRecords = locations.filter(isInterimDistrictLocation);

            setCompanyRegions(regions);
            setCompanyDistricts(districtRecords as unknown as District[]);
            setCompanyLocations(locations);

            // Build legacy grouped structure (Region → Locations) for backward compat
            const grouped = regions.map(region => {
                const regionLocations = locations.filter(loc => loc.regionalDivisionId === region.id);
                return { ...region, locations: regionLocations };
            }).sort((a, b) => a.name.localeCompare(b.name));

            setCompanyGroupedRegionsAndLocations(grouped as tGroupedRegionAndLocation[]);

            // Region → Districts (top-level Locations) → nested Locations (parent-encoded)
            const groupedWithDistricts: tGroupedRegionWithDistricts[] = regions.map(region => {
                const regionLocations = locations.filter(loc => loc.regionalDivisionId === region.id);
                const regionDistricts = districtRecords.filter(d => d.regionalDivisionId === region.id);
                const nestedLocations = regionLocations.filter(loc => getInterimDistrictParentId(loc));

                const groupedDistricts: tGroupedDistrict[] = regionDistricts.map(district => {
                    const districtLocations = nestedLocations.filter(
                        loc => getInterimDistrictParentId(loc) === district.id,
                    );
                    return { ...(district as unknown as District), locations: districtLocations };
                }).sort((a, b) => a.name.localeCompare(b.name));

                return {
                    ...region,
                    districts: groupedDistricts,
                    locations: regionLocations,
                };
            }).sort((a, b) => a.name.localeCompare(b.name));

            setCompanyGroupedData(groupedWithDistricts);

            await fetchAllRegionAndLocationData();

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const resetRegionAndLocation = (): void => {
        setCompanyRegions([]);
        setCompanyLocations([]);
        setCompanyDistricts([]);
        setCompanyGroupedRegionsAndLocations([]);
        setCompanyGroupedData([]);
        setLoading(false);
    };

    const updateRegionInDatabase = async (regionalDivisionId: string, updates: Partial<RegionalDivision>) => {
        try {
            const { __typename, createdAt: _createdAt, updatedAt: _updatedAt, owner: _owner, locations: _locations, districts: _districts, ...cleanUpdates } = updates as any;

            await authClient.models.RegionalDivision.update({
                id: regionalDivisionId,
                ...cleanUpdates,
            });

            updateRegion(regionalDivisionId, updates);
        } catch (error) {
            console.error(`Error updating regionId ${regionalDivisionId}:`, error);
            throw error;
        }
    };

    const updateLocationInDatabase = async (locationId: string, updates: Partial<Location>) => {
        try {
            const { __typename, createdAt: _createdAt, updatedAt: _updatedAt, owner: _owner, ...cleanUpdates } = updates as any;

            await authClient.models.Location.update({
                id: locationId,
                ...cleanUpdates,
            });

            updateLocation(locationId, updates);

        } catch (error) {
            console.error(`Error updating locationId ${locationId}:`, error);
            throw error;
        }
    };

    // ─── Region CRUD ───

    const addRegion = (region: RegionalDivision): void => {
        setCompanyRegions(prev => [...prev, region]);
        setCompanyGroupedData(prev => [...prev, { ...region, districts: [], locations: [] }]);
    };

    const updateRegion = (id: string, updates: Partial<RegionalDivision>): void => {
        setCompanyRegions(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
        setCompanyGroupedData(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
    };

    // ─── District CRUD ───

    const addDistrict = (district: District): void => {
        setCompanyDistricts(prev => [...prev, district]);
        setCompanyGroupedData(prev => prev.map(region => {
            if (region.id === district.regionalDivisionId) {
                return { ...region, districts: [...region.districts, { ...district, locations: [] }] };
            }
            return region;
        }));
    };

    const updateDistrict = (id: string, updates: Partial<District>): void => {
        setCompanyDistricts(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
        setCompanyGroupedData(prev => prev.map(region => ({
            ...region,
            districts: region.districts.map(d => d.id === id ? { ...d, ...updates } : d),
        })));
    };

    const deleteDistrict = (id: string): void => {
        setCompanyDistricts(prev => prev.filter(d => d.id !== id));
        setCompanyGroupedData(prev => prev.map(region => ({
            ...region,
            districts: region.districts.filter(d => d.id !== id),
        })));
    };

    // ─── Location CRUD ───

    const addLocation = (location: Location): void => {
        setCompanyLocations(prev => [...prev, location]);
        setAllLocations(prev => [...prev, location]);
        const parentDistrictId = getInterimDistrictParentId(location);
        setCompanyGroupedData(prev => prev.map(region => ({
            ...region,
            locations: region.id === location.regionalDivisionId ? [...region.locations, location] : region.locations,
            districts: region.districts.map(d =>
                d.id === parentDistrictId ? { ...d, locations: [...d.locations, location] } : d
            ),
        })));
    };

    const updateLocation = (id: string, updates: Partial<Location>): void => {
        setCompanyLocations(prev => prev.map(loc => loc.id === id ? { ...loc, ...updates } : loc));
        setAllLocations(prev => prev.map(loc => loc.id === id ? { ...loc, ...updates } : loc));
        setCompanyGroupedData(prev => prev.map(region => ({
            ...region,
            locations: region.locations.map(loc => loc.id === id ? { ...loc, ...updates } : loc),
            districts: region.districts.map(d => ({
                ...d,
                locations: d.locations.map(loc => loc.id === id ? { ...loc, ...updates } : loc),
            })),
        })));
    };

    const value: iRegionAndLocationContext = {
        allRegions,
        allLocations,
        companyRegions,
        companyLocations,
        companyDistricts,
        companyGroupedRegionsAndLocations,
        companyGroupedData,
        loading,
        refreshRegionAndLocation,
        resetRegionAndLocation,
        addRegion,
        updateRegion,
        addDistrict,
        updateDistrict,
        deleteDistrict,
        addLocation,
        updateLocation,
        updateLocationInDatabase,
        updateRegionInDatabase,
    };

    return (
        <RegionAndLocationContext.Provider value={value}>{children}</RegionAndLocationContext.Provider>
    );
};
